/**
 * t446u: re-order channels inside a section (or among channels without one). Shared by the desktop sidebar, the phone
 * web Channels list and the iOS app. The new order shows at once (optimistic), is put back if the save fails, and the
 * server tells everyone's other devices to refetch (chat_sections.changed).
 */
import { useCallback, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { applyChannelOrder, channelOrderBody, type Conversation } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { qk } from './keys';

type ChatList = { conversations: Conversation[] };

/** Saves `ids` (one group, in their new order). Resolves true when saved, false when it failed and was put back. */
export async function saveChannelOrder(qc: QueryClient, ws: string, sectionId: string | null, ids: string[]): Promise<boolean> {
  const key = qk.conversations(ws);
  await qc.cancelQueries({ queryKey: key });
  const before = qc.getQueryData<ChatList>(key);
  if (before) qc.setQueryData<ChatList>(key, { ...before, conversations: applyChannelOrder(before.conversations, ids) });
  try {
    await dataApi().reorderChannels(ws, channelOrderBody(sectionId, ids));
    return true;
  } catch {
    if (before) qc.setQueryData<ChatList>(key, before);
    return false;
  } finally {
    void qc.invalidateQueries({ queryKey: key });
  }
}

export function useReorderChannels(ws: string | null | undefined, onFailed?: () => void) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(0);
  const reorder = useCallback(async (sectionId: string | null, ids: string[]) => {
    if (!ws) return false;
    setSaving(n => n + 1);
    const ok = await saveChannelOrder(qc, ws, sectionId, ids);
    setSaving(n => n - 1);
    if (!ok) onFailed?.();
    return ok;
  }, [qc, ws, onFailed]);
  return { reorder, saving: saving > 0 };
}
